import React from 'react';
import { StyleSheet, css } from 'aphrodite';

const vacancies = [
  {
    title: 'Professional Chef',
    summary:
      'Fortress Restaurant is looking for a confident, creative chef who can lead in the kitchen and represent the Fortress brand in public.',
    details: [
      ['Position', 'Professional Chef'],
      ['Location', 'Fortress Menlyn Maine, Pretoria East'],
      [
        'Experience',
        'Five years of professional kitchen experience. Kitchen management experience will be an added advantage.',
      ],
      [
        'Creativity',
        'Able to respond creatively under pressure and prepare an exceptional dish based on an unexpected guest request.',
      ],
      [
        'Media Presence',
        'Confident, outgoing and comfortable creating social media videos, appearing on podcasts and engaging with an audience.',
      ],
    ],
    isNew: true,
  },
  {
    title: 'Waiter / Delivery Rider',
    summary:
      'We are looking for a motivated, customer-focused waiter. Scooter or motorcycle riding experience is an added advantage for assisting with YaBatho delivery orders.',
    details: [
      ['Position', 'Waiter / Delivery Rider'],
      ['Location', 'Fortress Menlyn Maine, Pretoria East'],
      [
        'Experience',
        'Restaurant or hospitality experience. Scooter or motorcycle riding experience is an added advantage.',
      ],
      [
        'Ideal Candidate',
        'Friendly, reliable, well-spoken, professional and able to work in a fast-paced restaurant environment.',
      ],
    ],
  },
];

const VacancyCard = ({ vacancy }) => (
  <article className={css(styles.card)}>
    <div className={css(styles.cardHeader)}>
      <p className={css(styles.kicker)}>
        {vacancy.isNew ? 'Newest Opening' : 'Available Opening'}
      </p>
      {vacancy.isNew && <span className={css(styles.newBadge)}>New</span>}
    </div>

    <h3 className={css(styles.cardTitle)}>{vacancy.title}</h3>
    <p className={css(styles.summary)}>{vacancy.summary}</p>

    <div className={css(styles.infoList)}>
      {vacancy.details.map(([label, value]) => (
        <div className={css(styles.infoItem)} key={label}>
          <span className={css(styles.infoLabel)}>{label}</span>
          <p className={css(styles.infoValue)}>{value}</p>
        </div>
      ))}
    </div>
  </article>
);

const Vacancies = () => (
  <section className={css(styles.section)} aria-labelledby="openings-title">
    <div className={css(styles.heading)}>
      <p className={css(styles.kicker)}>Current Opportunities</p>
      <h2 id="openings-title" className={css(styles.title)}>
        Available Vacancies
      </h2>
    </div>

    <div className={css(styles.grid)}>
      {vacancies.map((vacancy) => (
        <VacancyCard vacancy={vacancy} key={vacancy.title} />
      ))}
    </div>
  </section>
);

const styles = StyleSheet.create({
  section: {
    marginBottom: '3rem',
  },
  heading: {
    textAlign: 'center',
    marginBottom: '1.6rem',
  },
  kicker: {
    color: 'var(--color-golden)',
    fontFamily: 'var(--font-base)',
    fontSize: '20px',
    fontWeight: 400,
    letterSpacing: '0.04em',
    textTransform: 'capitalize',
    marginBottom: '0.8rem',
  },
  title: {
    color: 'var(--color-green)',
    fontFamily: 'var(--font-base)',
    fontSize: '40px',
    lineHeight: '52px',
    fontWeight: 400,
    letterSpacing: '0.04em',
    textTransform: 'capitalize',
    '@media (max-width: 650px)': {
      fontSize: '32px',
      lineHeight: '42px',
    },
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '2rem',
    alignItems: 'stretch',
    '@media (max-width: 950px)': {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    padding: '2rem',
    border: '1px solid rgba(200, 178, 137, 0.38)',
    borderRadius: '26px',
    background: 'var(--color-paper)',
    boxShadow: 'var(--shadow-soft)',
    '@media (max-width: 650px)': {
      padding: '1.4rem',
      borderRadius: '20px',
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
  },
  newBadge: {
    flexShrink: 0,
    padding: '0.35rem 0.8rem',
    borderRadius: '999px',
    color: '#081f1e',
    background: 'var(--color-golden)',
    fontFamily: 'var(--font-alt)',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  cardTitle: {
    color: 'var(--color-green)',
    fontFamily: 'var(--font-base)',
    fontSize: '34px',
    lineHeight: '44px',
    fontWeight: 400,
    letterSpacing: '0.04em',
    textTransform: 'capitalize',
    marginBottom: '1.5rem',
    '@media (max-width: 650px)': {
      fontSize: '28px',
      lineHeight: '38px',
    },
  },
  summary: {
    color: 'var(--color-grey)',
    fontFamily: 'var(--font-alt)',
    fontSize: '15px',
    lineHeight: '26px',
    letterSpacing: '0.04em',
    margin: '-0.5rem 0 1.5rem',
  },
  infoList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.2rem',
  },
  infoItem: {
    paddingBottom: '1rem',
    borderBottom: '1px solid rgba(35,79,64,.12)',
    ':last-child': {
      borderBottom: 'none',
      paddingBottom: 0,
    },
  },
  infoLabel: {
    display: 'block',
    color: 'var(--color-golden)',
    fontFamily: 'var(--font-base)',
    fontSize: '18px',
    letterSpacing: '0.04em',
    marginBottom: '0.35rem',
  },
  infoValue: {
    color: 'var(--color-gray)',
    fontFamily: 'var(--font-alt)',
    fontSize: '16px',
    lineHeight: '28px',
    letterSpacing: '0.04em',
    '@media (max-width: 650px)': {
      fontSize: '14px',
    },
  },
});

export default Vacancies;
